import { useState } from 'react';

import { EntityRefUtil, FormDTO } from '@gearbelt/global';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn, initials } from '@/lib/utils';
import { getFormRevisionsQueryOptions } from '@/queries/forms';

import { FormRevisionDiffModal } from './form-revision-diff-modal';
import { revisionActivityLabel } from './revision-decorations';

export function FormRevisionHistory({ form }: { form?: FormDTO }) {
  const [selectedRevision, setSelectedRevision] = useState<number>();

  const { data: revisions = [], isLoading } = useQuery(getFormRevisionsQueryOptions({ formId: form?.id }));

  const current = revisions.find((revision) => revision.revision === selectedRevision);
  const previous = current ? revisions.find((revision) => revision.revision === current.revision - 1) : undefined;

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <div>
        <h2 className="text-medium font-semibold">Activity</h2>
        {form && (
          <div className="text-secondary-text text-xs">
            Template version: <span className="text-default-text font-semibold">v{form.revision + 1}</span>
          </div>
        )}
      </div>
      <div className="border-grey-3 border-t" />

      {isLoading ? (
        <div className="text-secondary-text text-sm">Loading…</div>
      ) : (
        <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
          {revisions.map((revision) => {
            const isOriginal = revision.revision === 0;
            const isSelected = revision.revision === selectedRevision;
            const creatorName = EntityRefUtil.createUserFullNameRef(revision.createdBy, '-');
            const label = revisionActivityLabel(revision.revision);

            return (
              <li key={revision.revision}>
                <button
                  type="button"
                  disabled={isOriginal}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedRevision(revision.revision)}
                  className={cn(
                    'flex w-full items-start justify-between gap-3 rounded p-2 text-left',
                    !isOriginal && 'hover:bg-grey-1 cursor-pointer',
                    isSelected && 'bg-grey-1',
                  )}>
                  <div className="flex items-start gap-2">
                    <Avatar>
                      {revision.createdBy ? (
                        <>
                          <AvatarImage src={revision.createdBy.avatarUrl ?? undefined} />
                          <AvatarFallback>{initials(creatorName)}</AvatarFallback>
                        </>
                      ) : (
                        <AvatarFallback>-</AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex flex-col gap-1">
                      <div className="text-sm font-bold">{creatorName}</div>
                      <div className="border-grey-5 text-grey-5 flex h-[25px] items-center border-l-[3px] pl-5 text-xs italic">{label}</div>
                    </div>
                  </div>
                  <div className="text-grey-5 text-xs">{formatDistanceToNow(new Date(revision.createdAt), { addSuffix: true })}</div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {form && current && previous && (
        <FormRevisionDiffModal open onClose={() => setSelectedRevision(undefined)} form={form} previous={previous} current={current} />
      )}
    </div>
  );
}
